import { Lead, GuideSection, H3, P, UL, OL, LI, GuideLink } from './guide-ui';

/**
 * English-language guide (locale: 'en'): NIS2 and the Dutch
 * Cyberbeveiligingswet for international readers operating in the
 * Netherlands. A faithful adaptation of the Dutch Cbw guide (same sections,
 * same facts), written for expats and international companies with a Dutch
 * entity who do not read Dutch. Dutch legal terms are kept, with an English
 * explanation on first use.
 */
export function Nis2NetherlandsGuideBody() {
  return (
    <>
      <Lead>
        NIS2 is the European cybersecurity directive that the Netherlands has
        implemented through the Cyberbeveiligingswet (Cbw, the Dutch
        Cybersecurity Act), in force from 15 August 2026. If you run or work
        for an organisation that operates in the Netherlands but you do not
        read Dutch, this guide is for you: it explains who is covered, what
        the law requires, and what that means in practice for your logging
        and monitoring, with the Dutch terminology you will encounter along
        the way.
      </Lead>

      <GuideSection id="what-is-the-cbw" title="From the NIS2 directive to the Cyberbeveiligingswet">
        <P>
          NIS2 (Directive (EU) 2022/2555) is the successor to the first NIS
          directive and had to be transposed into national law by 17 October
          2024. The Netherlands does so with the{' '}
          <strong>Cyberbeveiligingswet (Cbw)</strong>, the Dutch Cybersecurity
          Act, which replaces the earlier Wet beveiliging netwerk- en
          informatiesystemen (Wbni, the Network and Information Systems
          Security Act). The Netherlands missed that deadline, but the law is
          now here:{' '}
          <strong>the Cyberbeveiligingswet enters into force on 15 August 2026</strong>.
        </P>
        <P>
          There is no transition period. From that date, the duty of care
          (zorgplicht), the incident notification duty (meldplicht) and the
          registration duty (registratieplicht) with the NCSC, the Dutch
          national cyber security centre, apply immediately, and the
          management board is personally responsible for compliance. The
          question is no longer whether you are preparing, but whether you can
          demonstrate today that you manage your risks, and whether you would
          spot and report an incident within 24 hours.
        </P>
        <P>
          The biggest shift compared with the Wbni: that law covered a few
          hundred providers of vital services. The Cyberbeveiligingswet
          covers more than 8,000 Dutch organisations across eighteen sectors,
          including mid-sized companies that never considered themselves
          &ldquo;critical infrastructure&rdquo;. If your company has a Dutch
          entity in one of those sectors, there is a real chance it is in
          scope.
        </P>
      </GuideSection>

      <GuideSection id="who-is-covered" title="Is my organisation covered?">
        <P>
          The law distinguishes two categories, with different supervision
          regimes and different fines:
        </P>
        <UL>
          <LI>
            <strong>Essential entities</strong> (essentiële entiteiten): large
            organisations in sectors such as energy, transport, banking,
            financial market infrastructure, healthcare, drinking water and
            waste water, digital infrastructure, public administration and
            space.
          </LI>
          <LI>
            <strong>Important entities</strong> (belangrijke entiteiten):
            medium-sized and large organisations in, among others, postal and
            courier services, waste management, chemicals, food, manufacturing
            (including medical devices and electronics), digital providers and
            research.
          </LI>
        </UL>
        <P>
          The size threshold is, as a baseline, around 50 employees or 10
          million euro in annual turnover. Smaller organisations can still be
          covered, for instance as the sole provider of an essential service
          or as part of a larger group: a Dutch subsidiary of an international
          company is assessed in that group context. There is also a{' '}
          <strong>registration duty</strong> (registratieplicht): organisations
          covered by the law must register with the supervisory authority.
        </P>
        <P>
          Not sure? The Dutch central government (Rijksoverheid) offers an
          online <strong>NIS2 self-assessment</strong> (NIS2-zelfevaluatie)
          that lets you formally check in a few minutes whether your
          organisation falls under the law, and in which category. The tool is
          available in English.
        </P>
      </GuideSection>

      <GuideSection id="duty-of-care" title="The duty of care: which measures must you take?">
        <P>
          The core of the law is the duty of care (zorgplicht): appropriate
          and proportionate technical and organisational measures to manage
          the risks to your network and information systems. The directive
          explicitly lists, among others:
        </P>
        <UL>
          <LI>risk analysis and information security policies;</LI>
          <LI>
            <strong>incident handling</strong>: being able to detect, analyse,
            contain and recover, and being able to prove it;
          </LI>
          <LI>business continuity, backups and crisis management;</LI>
          <LI>supply chain security;</LI>
          <LI>security in the development, procurement and maintenance of systems;</LI>
          <LI>policies to measure the effectiveness of your measures;</LI>
          <LI>cyber hygiene and security training;</LI>
          <LI>cryptography and, where appropriate, encryption;</LI>
          <LI>access control, personnel security and asset management;</LI>
          <LI>multi-factor authentication and secured communications.</LI>
        </UL>
        <P>
          New compared with the previous regime is the{' '}
          <strong>personal responsibility of the management board</strong>
          {' '}(bestuurdersverantwoordelijkheid): directors must approve the
          measures, oversee their implementation, and complete cybersecurity
          training themselves. In cases of serious negligence, directors can
          be held personally liable.
        </P>
      </GuideSection>

      <GuideSection id="incident-reporting" title="The notification duty: 24 hours, 72 hours, one month">
        <P>
          Significant incidents must be reported to the national CSIRT (for
          most organisations the NCSC) and the supervisory authority, in three
          steps:
        </P>
        <OL>
          <LI>
            <strong>Within 24 hours</strong>: an early warning, with a first
            assessment of whether malicious action is suspected and whether
            the incident could have cross-border impact.
          </LI>
          <LI>
            <strong>Within 72 hours</strong>: a full incident notification
            with an initial assessment of severity and impact and, where
            available, indicators of compromise (IOCs).
          </LI>
          <LI>
            <strong>Within one month</strong>: a final report with a detailed
            description of the incident, the likely root cause, the measures
            taken and any cross-border impact.
          </LI>
        </OL>
        <P>
          Those deadlines are short. If you only start collecting logs after
          an incident, you will miss them: saying anything meaningful about
          malicious action within 24 hours requires that the relevant logs
          already exist, are searchable, and that detection noticed the
          incident in the first place. See our guide on{' '}
          <GuideLink href="/guides/nis2-logging-requirements">
            NIS2 logging requirements
          </GuideLink>{' '}
          for what that means in detail.
        </P>
      </GuideSection>

      <GuideSection id="supervision-and-fines" title="Supervision, fines and liability">
        <P>
          The <strong>Rijksinspectie Digitale Infrastructuur (RDI)</strong>,
          the Dutch Authority for Digital Infrastructure, is the supervisory
          authority for many sectors; in addition, sector regulators supervise
          their own domain, such as DNB (the Dutch central bank) for the
          financial sector. Essential entities face proactive supervision
          (audits and inspections up front); important entities face reactive
          supervision (after signals or incidents).
        </P>
        <P>The fines are GDPR-grade:</P>
        <UL>
          <LI>
            essential entities: up to <strong>10 million euro or 2%</strong>{' '}
            of worldwide annual turnover;
          </LI>
          <LI>
            important entities: up to <strong>7 million euro or 1.4%</strong>{' '}
            of worldwide annual turnover.
          </LI>
        </UL>
        <P>
          In addition, the supervisory authority can issue binding
          instructions and, for essential entities, as a last resort have
          directors temporarily suspended from their duties.
        </P>
      </GuideSection>

      <GuideSection id="logging-and-monitoring" title="What this means in practice for logging and monitoring">
        <P>
          Logging and monitoring appear in almost every obligation: you cannot
          detect an incident, substantiate a 24-hour notification or write a
          final report without the right logs. In practice, the duty of care
          comes down to this:
        </P>
        <H3>Centralise your logs</H3>
        <P>
          Logs from servers, network equipment, identity providers, cloud
          services and applications belong in one searchable system.
          Reconstructing an incident across ten separate systems is not
          something you do within 72 hours.
        </P>
        <H3>Retain them long enough, demonstrably</H3>
        <P>
          The law does not set a fixed retention period; the criterion is that
          you can investigate and report incidents. In practice, 6 to 18
          months is common, with a searchable hot tier and a cheaper archive.
          Document your choice and your reasoning: that is exactly what a
          supervisory authority will ask for.
        </P>
        <H3>Monitor in real time, not after the fact</H3>
        <P>
          The notification deadlines assume you see incidents as they happen.
          That requires detection rules on your logs (failed login attempts,
          anomalous behaviour, suspicious network connections) and alerting
          that reaches a human in time, without drowning in false positives.
        </P>
        <H3>Keep an audit trail</H3>
        <P>
          Who did what, and when, including inside your logging platform
          itself? A tamper-evident audit trail underpins both your incident
          reporting and your accountability towards the supervisory
          authority.
        </P>
        <P>
          This is the side LogPulse covers: centralised log management with
          configurable retention per plan, 50+ built-in detections with MITRE
          ATT&amp;CK mapping in the{' '}
          <GuideLink href="/siem">LogPulse SIEM</GuideLink>, risk-based
          alerting that surfaces a handful of real incidents instead of
          thousands of notifications, and compliance reports that map your
          detections and data to NIS2 obligations. All data stays in the EU
          (GCP Amsterdam), which considerably simplifies accountability
          towards the regulator and your data processing agreements. To be
          clear: no product makes you compliant by itself, but the logging,
          detection and reporting foundation is the part you can put in place
          today.
        </P>
      </GuideSection>

      <GuideSection id="getting-started" title="Getting started: a step-by-step plan">
        <OL>
          <LI>
            <strong>Establish whether you are covered</strong> using the
            Rijksoverheid NIS2 self-assessment, and in which category.
          </LI>
          <LI>
            <strong>Inform the board</strong>: they will be personally
            responsible and must approve the approach.
          </LI>
          <LI>
            <strong>Run a risk analysis</strong> and map which systems and
            suppliers are critical to your services.
          </LI>
          <LI>
            <strong>Centralise logging and set up detection</strong>: this is
            the foundation under both the duty of care and the notification
            duty.
          </LI>
          <LI>
            <strong>Rehearse the notification procedure</strong>: know who
            informs the NCSC and the supervisory authority within 24 hours,
            and with what information.
          </LI>
          <LI>
            <strong>Document everything</strong>: measures, decisions,
            retention periods and exercises. Compliance you cannot
            demonstrate does not exist as far as a regulator is concerned.
          </LI>
        </OL>
        <P>
          Want to know how LogPulse fills in the logging, detection and
          reporting side? See{' '}
          <GuideLink href="/compliance/nis2">NIS2 compliance with LogPulse</GuideLink>{' '}
          and our <GuideLink href="/pricing">pricing</GuideLink>, or start for
          free and set up your first detections today.
        </P>
      </GuideSection>
    </>
  );
}
